import type { IVariable, IVariableType } from '@carboncopyco/types';
import { useRef, useState } from 'react';
import {
  RulesContent,
  RulesManager,
  type RulesManagerProps,
} from '../RulesManager';
import { AddVariableButton } from './AddVariableButton';
import { VariablesContent } from './VariablesContent';

export interface VariablesManagerProps
  extends Pick<RulesManagerProps, 'isOpen' | 'onClose' | 'onLearnMoreClick'> {
  variables: IVariable[];
  createVariable: (variable: IVariable) => Promise<unknown>;
  editVariable: (variable: IVariable) => Promise<unknown>;
  deleteVariable: (variable: IVariable) => Promise<unknown>;
  reorderVariables: (moved: IVariable, after: IVariable) => Promise<unknown>;
  onUseVariable: (variable: IVariable) => Promise<unknown>;
}

const helpLink = 'https://help.demostack.com/en/articles/5919207';

export const VariablesManager = ({
  isOpen,
  onClose,
  variables,
  createVariable,
  editVariable,
  deleteVariable,
  reorderVariables,
  onUseVariable,
  onLearnMoreClick,
}: VariablesManagerProps) => {
  const [variableToCreate, setVariableToCreate] = useState<IVariableType>();
  const contentRef = useRef<HTMLDivElement>();

  const showEmptyState = !variables.length && !variableToCreate;

  return (
    <RulesManager
      isOpen={isOpen}
      onClose={!variableToCreate ? onClose : undefined}
      onLearnMoreClick={onLearnMoreClick}
    >
      <RulesManager.Header>
        <RulesManager.Header.Title label="Variables" />
        <RulesManager.Header.Action>
          <AddVariableButton
            variables={variables}
            onSelect={(variableType) => {
              setVariableToCreate(variableType);
              contentRef.current?.scrollTo({ top: 0 });
            }}
          />
        </RulesManager.Header.Action>
      </RulesManager.Header>
      {!showEmptyState && (
        <div className="mt-2 mb-5">
          <RulesManager.Subheader
            label="Use personalized placeholders."
            linkLabel="Learn how"
            linkUrl={helpLink}
          />
        </div>
      )}
      {showEmptyState ? (
        <RulesManager.EmptyState color="yellow">
          <RulesManager.EmptyState.Title label="Use personalized placeholders" />
          <RulesManager.EmptyState.Subtitle
            label="Not sure how it works?"
            linkLabel="Learn more"
            linkUrl={helpLink}
          />
        </RulesManager.EmptyState>
      ) : (
        <RulesContent>
          <RulesManager.Content ref={contentRef}>
            <VariablesContent
              variableToCreate={variableToCreate}
              variables={variables}
              createVariable={async (variable) => {
                await createVariable(variable);
                setVariableToCreate(null);
              }}
              editVariable={editVariable}
              deleteVariable={deleteVariable}
              reorderVariables={reorderVariables}
              cancelCreation={() => setVariableToCreate(null)}
              onUseVariable={onUseVariable}
            />
          </RulesManager.Content>
        </RulesContent>
      )}
    </RulesManager>
  );
};
